// ─────────────────────────────────────────────
// KLOTHIQ — Supplier Form Page (wired)
// ─────────────────────────────────────────────

import { Input, Textarea } from '@/components/ui/FormControls';
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import { useSupplier, useCreateSupplier, useUpdateSupplier } from '@/hooks/useSuppliers';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const supplierSchema = z.object({
  name:           z.string().min(2, 'Name must be at least 2 characters'),
  phone:          z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number').or(z.literal('')).optional(),
  email:          z.string().email('Enter a valid email').or(z.literal('')).optional(),
  address:        z.string().optional(),
  city:           z.string().optional(),
  contact_person: z.string().optional(),
  notes:          z.string().optional(),
});

type SupplierFormValues = z.infer<typeof supplierSchema>;

const inputCls = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50';

function Field({ id, label, error, required, children }: {
  id: string; label: string; error?: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}{required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function SupplierFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: existing, isLoading, error } = useSupplier(id);
  const create = useCreateSupplier();
  const update = useUpdateSupplier();

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<SupplierFormValues>({ resolver: zodResolver(supplierSchema) });

  useEffect(() => {
    if (existing) {
      reset({
        name: existing.name,
        phone: existing.phone ?? '',
        email: existing.email ?? '',
        address: existing.address ?? '',
        city: existing.city ?? '',
        contact_person: existing.contact_person ?? '',
        notes: existing.notes ?? '',
      });
    }
  }, [existing, reset]);

  const onSubmit = async (values: SupplierFormValues) => {
    try {
      if (isEditing) {
        await update.mutateAsync({ id, ...values });
        toast.success('Supplier updated successfully');
      } else {
        await create.mutateAsync(values);
        toast.success('Supplier created successfully');
      }
      navigate('/suppliers');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  if (isLoading && isEditing) return <LoadingState message="Loading supplier…" />;
  if (error) return <ErrorState message={error.message} />;

  const isWorking = isSubmitting || create.isPending || update.isPending;

  return (
    <div>
      <PageHeader
        title={isEditing ? 'Edit Supplier' : 'New Supplier'}
        description={isEditing ? 'Update supplier details' : 'Add a new supplier'}
        backTo="/suppliers"
      />
      <div className="mx-auto max-w-lg">
        <div className="obsidian-surface rounded-lg border border-border bg-card p-6 shadow-sm">
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <Field id="supplier-name" label="Name" required error={errors.name?.message}>
              <Input
                id="supplier-name"
                {...register('name')}
                placeholder="Supplier name"
                className={inputCls}
                aria-invalid={!!errors.name}
                helpLabel="Name"
              />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field id="supplier-phone" label="Phone" error={errors.phone?.message}>
                <Input
                  id="supplier-phone"
                  type="tel"
                  {...register('phone')}
                  placeholder="10-digit mobile"
                  maxLength={10}
                  className={inputCls}
                  helpLabel="Phone"
                />
              </Field>
              <Field id="supplier-email" label="Email" error={errors.email?.message}>
                <Input
                  id="supplier-email"
                  type="email"
                  {...register('email')}
                  placeholder="email@example.com"
                  className={inputCls}
                  helpLabel="Email"
                />
              </Field>
            </div>

            <Field id="supplier-address" label="Address" error={errors.address?.message}>
              <Textarea
                id="supplier-address"
                {...register('address')}
                rows={2}
                placeholder="Full address"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                helpLabel="Address"
              />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field id="supplier-city" label="City" error={errors.city?.message}>
                <Input
                  id="supplier-city"
                  {...register('city')}
                  placeholder="City"
                  className={inputCls}
                  helpLabel="City"
                />
              </Field>
              <Field id="supplier-contact" label="Contact Person" error={errors.contact_person?.message}>
                <Input
                  id="supplier-contact"
                  {...register('contact_person')}
                  placeholder="Contact name"
                  className={inputCls}
                  helpLabel="Contact Person"
                />
              </Field>
            </div>

            <Field id="supplier-notes" label="Notes" error={errors.notes?.message}>
              <Textarea
                id="supplier-notes"
                {...register('notes')}
                rows={2}
                placeholder="Optional notes"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                helpLabel="Notes"
              />
            </Field>

            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <button type="button" onClick={() => navigate('/suppliers')} className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent">Cancel</button>
              <button type="submit" disabled={isWorking} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                {isWorking && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEditing ? 'Update' : 'Create'} Supplier
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
